import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Link,
  useTheme,
  useMediaQuery,
  alpha,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_CONFIG } from "../../config/api";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoginIcon from "@mui/icons-material/Login";
import ForgotPassword from "./ForgotPassword";
import { apiService } from "../../services/api";
import { login, signUp } from "../../services/backendAPI";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { REGISTRATION_URL } from "../../utils/constant";
import { getAssetUrl } from '../../config/api';

const Login = ({ onLogin, onVerify }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [redirectMessage, setRedirectMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const navigate = useNavigate();

  let tradexLogo = getAssetUrl('TradexLogoFull.svg');

  useEffect(() => {
    if (countdown !== null) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleRedirect();
      }
    }
  }, [countdown]);

  const handleRedirect = () => {
    if (redirectMessage.includes("reset password")) {
      setShowForgotPassword(true);
    } else if (redirectMessage.includes("reset PIN")) {
      // Navigate to reset PIN page
      // TODO: Implement reset PIN page
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: name === "username" ? value.toUpperCase() : value,
    }));

    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await login(credentials.username, credentials.password);
      console.log("response",response);
      if (response.status === "success") {
        // Store basic auth data
        // localStorage.setItem('token', response.data.auth_token);
        localStorage.setItem("login_id", response.data.login_id);

        // Handle password reset requirement
        if (response.data.reset_password) {
          setRedirectMessage("Redirecting to reset password page in");
          setCountdown(3);
          return;
        }

        // Handle 2FA reset requirement
        if (response.data.reset_twofa) {
          setRedirectMessage("Redirecting to reset PIN page in");
          setCountdown(3);
          return;
        }

        // Handle normal flow
        if (response.data.twofa_enabled) {
          // Store 2FA data and proceed to 2FA
          localStorage.setItem("twofa_token", response.data.twofa.twofa_token);
          localStorage.setItem(
            "twofa_questions",
            JSON.stringify(response.data.twofa.questions)
          );
          localStorage.setItem("twofa_type", response.data.twofa.type);
          localStorage.setItem("isAuthenticated", "true");
          onLogin();
        } else {
          // Skip 2FA and go directly to dashboard

          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("auth_token", response.data.auth_token);
          localStorage.setItem("2faVerified", "true");
          onLogin();
          onVerify();
        }
      } else {
        setError(response.message || "Invalid credentials");
        setSnackbarMessage(response.message || "Invalid credentials");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.log("err",err);
      setError("Login failed. Please try again.");
      setSnackbarMessage(err.message || "Login failed. Please try again.");
      setOpenSnackbar(true);
      console.error("Login error:", err);
    }
  };
  const handleSignUpClick = async () => {
    // Preserved original commented code
    // try {
    //   const response = await signUp(); // Make the API call

    //   if (response.status === "success") {
    //     enqueueSnackbar("Sign up initiated successfully!", { variant: "success" });

    //     // Navigate based on the message in the response
    //     if (response.data.step === "send-email-otp" || response.data.message === "send-email-otp") {
    //       navigate("/signup");
    //     } else if (response.data.step === "verify-email-otp") {
    //       navigate("/verify-otp");
    //     } else {
    //       enqueueSnackbar("Unknown step in response.", { variant: "warning" });
    //     }
    //   } else {
    //     enqueueSnackbar(response.message || "Failed to initiate sign-up.", {
    //       variant: "error",
    //     });
    //   }
    // } catch (error) {
    //   enqueueSnackbar(error.message || "An error occurred.", { variant: "error" });
    // }

    // Open registration URL in a new tab
    window.open(REGISTRATION_URL, "_blank");
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(45deg, ${alpha(
                theme.palette.primary.dark,
                0.1
              )}, ${alpha(theme.palette.background.default, 0.95)})`
            : `linear-gradient(45deg, ${alpha(
                theme.palette.primary.light,
                0.1
              )}, ${alpha(theme.palette.background.default, 0.95)})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: { xs: "2vh", sm: "4vh" },
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Logo outside the form */}
      <Box 
        sx={{ 
          mb: 0.5,
          width: { xs: '120px', sm: '140px', md: '160px' },
          textAlign: 'center'
        }}
      >
        <img
          src={tradexLogo}
          alt="TRADEX"
          style={{
            width: "100%",
            height: "auto",
            filter: theme.palette.mode === "dark" ? "none" : "none"
          }}
        />
      </Box>

      <Paper
        elevation={isMobile ? 2 : 4}
        sx={{
          width: "100%",
          maxWidth: "400px",
          borderRadius: "16px",
          background: theme.palette.background.paper,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            pt: { xs: 3, sm: 4 },
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontWeight: 600,
              letterSpacing: "0.5px",
              color: theme.palette.text.primary,
            }}
          >
            Sign In
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 1 }}
          >
            Sign in to continue to your account
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            p: { xs: 3, sm: 4 },
            pt: 2,
            pb: 3,
            gap: 2,
          }}
        >
          <TextField
            required
            fullWidth
            id="username"
            label="Client ID / Email / Phone"
            name="username"
            autoComplete="username"
            autoFocus
            value={credentials.username}
            onChange={handleChange}
            error={!!error}
            size="medium"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
              mt: 1
            }}
          />

          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={credentials.password}
            onChange={handleChange}
            error={!!error}
            size="medium"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {(error || redirectMessage) && (
            <Box sx={{ textAlign: "center" }}>
              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              {redirectMessage && (
                <Typography color="primary" variant="body2">
                  {redirectMessage} {countdown} seconds...
                </Typography>
              )}
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 1,
              py: 1.5,
              fontSize: "0.9375rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)",
              "&:hover": {
                background: "linear-gradient(90deg, #1565c0 0%, #1976d2 100%)",
              },
            }}
          >
            Sign In
          </Button>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: isMobile ? 1 : 0,
              mt: 1
            }}
          >
            <Link
              href="#"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                setShowForgotPassword(true);
              }}
              sx={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              Forgot password?
            </Link>
            <Link
              variant="body2"
              onClick={handleSignUpClick}
              sx={{
                cursor: "pointer",
                textDecoration: "none",
                color: theme.palette.primary.main,
                "&:hover": {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              Sign Up
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Add Snackbar at the end */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
